// Dinero siempre en centavos enteros. Nunca se opera con decimales flotantes.

export type Cents = number;

export interface MoneyFormatOptions {
  /** Mostrar centavos. Muchos negocios prefieren "$ 1.500" en vez de "$ 1.500,00". */
  showCents?: boolean;
  currency?: string;
}

const formatters = new Map<string, Intl.NumberFormat>();

function formatter(showCents: boolean, currency: string): Intl.NumberFormat {
  const key = `${currency}:${showCents}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency,
      minimumFractionDigits: showCents ? 2 : 0,
      maximumFractionDigits: showCents ? 2 : 0,
    });
    formatters.set(key, f);
  }
  return f;
}

export function formatMoney(cents: Cents, options: MoneyFormatOptions = {}): string {
  const { showCents = true, currency = 'ARS' } = options;
  assertCents(cents);
  return formatter(showCents, currency).format(cents / 100);
}

export function assertCents(value: number): asserts value is Cents {
  if (!Number.isSafeInteger(value)) {
    throw new RangeError(`Monto inválido (se esperaban centavos enteros): ${value}`);
  }
}

/**
 * Interpreta un monto escrito por una persona en formato argentino.
 * "1.234,56" → 123456 · "1234,5" → 123450 · "$ 1.500" → 150000
 * Tolerancia para teclados numéricos de celular que muestran punto: "12.5" y "12.50"
 * (un solo punto seguido de 1–2 dígitos) se toman como decimales.
 * Devuelve null si no es un monto válido.
 */
export function parseMoney(input: string): Cents | null {
  let s = input.replace(/\$|\s| /g, '');
  if (s === '') return null;
  const negative = s.startsWith('-');
  if (negative) s = s.slice(1);

  let intPart: string;
  let decPart = '';
  if (s.includes(',')) {
    const parts = s.split(',');
    if (parts.length !== 2) return null;
    intPart = parts[0]!;
    decPart = parts[1]!;
    if (intPart.includes('.') && !/^\d{1,3}(\.\d{3})+$/.test(intPart)) return null;
    intPart = intPart.replace(/\./g, '');
  } else if (/^\d+\.\d{1,2}$/.test(s)) {
    [intPart, decPart] = s.split('.') as [string, string];
  } else {
    if (s.includes('.') && !/^\d{1,3}(\.\d{3})+$/.test(s)) return null;
    intPart = s.replace(/\./g, '');
  }

  if (!/^\d+$/.test(intPart || '0') || !/^\d{0,2}$/.test(decPart)) return null;
  const cents = Number(intPart || '0') * 100 + Number(decPart.padEnd(2, '0') || '0');
  if (!Number.isSafeInteger(cents)) return null;
  return negative ? -cents : cents;
}

/** Aplica un porcentaje (puede ser negativo) con redondeo al centavo. */
export function applyPercent(cents: Cents, percent: number): Cents {
  return Math.round(cents * (1 + percent / 100));
}
